import React, { useEffect, useRef } from 'react';
import { Dialog, useDialogState } from 'reakit';
import styled, { css } from 'styled-components';
import { mediaQuery } from '../../css';
import { breakpoints, spaces } from '../../settings';
import { Box } from '../Box';
import { Card } from '../Card';
import { Backdrop, CloseButton, usePreventBodyScroll } from '../Dialog';
import { Heading } from '../Heading';

const pageGutter = '2bu';

export interface ModalProps {
    'aria-label': string;
    children: React.ReactNode;
    /**
     * An element ref to focus when the modal is shown.
     */
    initialFocusRef?: React.RefObject<HTMLElement>;
    /**
     * Callback fired when the modal is attempting to be closed.
     */
    onClose: () => void;
    size?: 'small' | 'medium' | 'auto';
    /**
     * Dictates if the modal is shown.
     */
    visible: boolean;
}

const AlignedBox = styled(Box)`
    align-items: flex-end;

    ${mediaQuery.minimum('medium')} {
        align-items: center;
    }
`;

const ModalCard = styled(Card).attrs(() => ({ appearance: 'elevated' }))<{
    size: ModalProps['size'];
}>`
    min-width: ${breakpoints.small[1] - spaces.px[pageGutter] * 2}px;
    overflow: hidden;
    position: relative;

    ${mediaQuery.only('small')} {
        border-bottom-left-radius: 0px;
        border-bottom-right-radius: 0px;
        min-width: 100%;
    }

    ${({ size }) => {
        switch (size) {
            case 'small':
                return css`
                    flex-grow: 1;
                    max-width: ${breakpoints.small[1] - spaces.px[pageGutter] * 2}px;
                `;

            case 'medium':
                return css`
                    flex-grow: 1;
                    max-width: ${breakpoints.medium[1] - spaces.px[pageGutter] * 2}px;
                `;

            case 'auto':
            default:
                return;
        }
    }}

    &:focus {
        outline: none;
    }
`;

/**
 * An accessible dialog component that follows the WAI-ARIA Dialog (Modal) pattern.
 *
 * Can be used with `ModalHeader`, `ModalContent`, and `ModalFooter` as needed.
 */
export function Modal({
    'aria-label': ariaLabel,
    children,
    initialFocusRef,
    onClose,
    size = 'auto',
    visible,
}: ModalProps) {
    const dialogRef = useRef<HTMLDivElement>(null);
    const dialogProps = useDialogState();

    const controlledDialogProps = {
        ...dialogProps,
        hide: onClose,
        visible,
    };

    // We're using custom body scroll logic (along with `preventBodyScroll`) due to a bug in reakit
    // When moving to ariakit, this logic can be removed.
    usePreventBodyScroll(controlledDialogProps.visible);

    useEffect(() => {
        if (visible) {
            if (initialFocusRef?.current) {
                initialFocusRef.current.focus();
            } else if (dialogRef?.current) {
                dialogRef.current.focus({
                    preventScroll: true,
                });
            }
        }
    }, [initialFocusRef, visible]);

    return (
        <Backdrop {...controlledDialogProps}>
            <AlignedBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                minHeight="100%"
                padding={['none', pageGutter, pageGutter]}
                paddingTop={pageGutter}
            >
                <Dialog
                    ref={dialogRef}
                    as={ModalCard}
                    aria-label={ariaLabel}
                    preventBodyScroll={false}
                    size={size}
                    {...controlledDialogProps}
                >
                    {children}
                    <CloseButton onClose={onClose} />
                </Dialog>
            </AlignedBox>
        </Backdrop>
    );
}

export const ModalHeader = ({ children }: { children: React.ReactNode }) => (
    <Box maxWidth="90%" marginBottom="1bu">
        <Heading accessibilityLevel={3} appearanceLevel={4}>
            {children}
        </Heading>
    </Box>
);

export const ModalContent = ({ children }: { children: React.ReactNode }) => <div>{children}</div>;

export const ModalFooter = ({ children }: { children: React.ReactNode }) => (
    <Box display="flex" justifyContent="flex-end" marginTop="1bu">
        {children}
    </Box>
);
